import { useState, useEffect } from 'react';
import api from '../lib/api';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogTitle } from './ui/dialog';
import { Award, X, Share2, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

/**
 * V13.14.2 P1.4 — MilestoneCelebration
 *
 * Polls /api/me/milestones for unseen milestones. When any exist, shows a
 * small celebration card inline on the dashboard AND auto-opens a one-time
 * confetti dialog for the most recent unseen one.
 *
 * Respects:
 *  • User can dismiss the card without losing streak data (soft-delete)
 *  • Auto-dialog fires at most once per milestone (seen_at persisted)
 *  • Safe no-op if backend 403/404 (e.g. admin account opens learner route)
 */
export default function MilestoneCelebration() {
  const [milestones, setMilestones] = useState([]);
  const [autoOpen, setAutoOpen] = useState(null); // milestone to pop-up
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const res = await api.get('/api/me/milestones', { params: { only_unseen: false, limit: 5 } });
      setMilestones(res.data.milestones || []);
      // Auto-popup the most-recent unseen one (if any)
      const unseen = (res.data.milestones || []).find((m) => !m.seen_at && !m.dismissed_at);
      if (unseen) setAutoOpen(unseen);
    } catch {
      // Silent — this is decorative. Admins hitting learner dash shouldn't
      // see an error toast here.
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const markSeen = async (id) => {
    try { await api.post(`/api/me/milestones/${id}/mark-seen`); }
    catch { /* noop */ }
    setMilestones((list) => list.map((m) => m.id === id ? { ...m, seen_at: new Date().toISOString() } : m));
    setAutoOpen(null);
  };

  const dismiss = async (id) => {
    try { await api.post(`/api/me/milestones/${id}/dismiss`); }
    catch { /* noop */ }
    setMilestones((list) => list.filter((m) => m.id !== id));
    if (autoOpen?.id === id) setAutoOpen(null);
  };

  const share = async (m) => {
    const text = `${m.badge} at Soul Sanskar 🎉 ${m.message}`;
    if (navigator.share) {
      try { await navigator.share({ title: 'Soul Sanskar milestone', text }); return; }
      catch { /* user cancelled */ }
    }
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Copied to clipboard — share away!');
    } catch { toast.info('Share not supported on this device'); }
  };

  if (loading || !milestones.length) return null;
  const visible = milestones.filter((m) => !m.dismissed_at);
  if (!visible.length) return null;

  return (
    <>
      <Card className="bg-gradient-to-br from-amber-50 via-rose-50 to-pink-50 border-amber-200 rounded-2xl overflow-hidden"
            data-testid="milestone-celebration-card">
        <CardContent className="pt-5 pb-4">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="h-4 w-4 text-amber-600" />
            <span className="text-xs font-medium text-amber-900 uppercase tracking-wide">
              {visible.length > 1 ? `${visible.length} milestones` : 'New milestone'}
            </span>
          </div>
          <div className="space-y-2">
            {visible.slice(0, 3).map((m) => (
              <div key={m.id} className="flex items-start gap-3 p-2 bg-white/60 rounded-lg"
                   data-testid={`milestone-row-${m.id}`}>
                <div className="h-8 w-8 rounded-full bg-amber-500/90 text-white flex items-center justify-center flex-shrink-0">
                  <Award className="h-4 w-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-amber-950 leading-tight">{m.badge}</p>
                  <p className="text-xs text-amber-900/80 line-clamp-2 mt-0.5">{m.message}</p>
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-amber-700 hover:text-amber-900"
                    onClick={() => share(m)} title="Share"
                    data-testid={`share-milestone-${m.id}`}>
                    <Share2 className="h-3.5 w-3.5" />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-amber-700 hover:text-amber-900"
                    onClick={() => dismiss(m.id)} title="Dismiss"
                    data-testid={`dismiss-milestone-${m.id}`}>
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Auto popup for the most recent unseen milestone */}
      <Dialog open={!!autoOpen} onOpenChange={(v) => !v && autoOpen && markSeen(autoOpen.id)}>
        <DialogContent className="sm:max-w-md text-center overflow-hidden" data-testid="milestone-popup">
          <DialogTitle className="sr-only">
            {autoOpen?.badge || 'Milestone achieved'}
          </DialogTitle>
          {autoOpen && (
            <div className="py-4 relative">
              <div className="absolute inset-0 pointer-events-none opacity-20"
                   style={{
                     background: 'radial-gradient(circle at 50% 30%, #fbbf24 0%, transparent 60%), radial-gradient(circle at 20% 70%, #f43f5e 0%, transparent 50%), radial-gradient(circle at 80% 80%, #ec4899 0%, transparent 50%)',
                   }} />
              <div className="relative">
                <div className="h-20 w-20 rounded-full bg-gradient-to-br from-amber-400 to-rose-500 mx-auto flex items-center justify-center shadow-lg">
                  <Award className="h-10 w-10 text-white" />
                </div>
                <h2 className="font-heading text-2xl font-semibold mt-4">{autoOpen.badge}</h2>
                <p className="text-sm text-muted-foreground mt-2 px-4">{autoOpen.message}</p>
                <div className="flex items-center justify-center gap-2 mt-5">
                  <Button variant="outline" onClick={() => markSeen(autoOpen.id)} data-testid="milestone-close-btn">
                    Thanks!
                  </Button>
                  <Button onClick={() => share(autoOpen)} data-testid="milestone-share-btn">
                    <Share2 className="h-3.5 w-3.5 mr-1" />Share the joy
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
